import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CHIP_METER_KEYS, PlanLimitKey, isUnlimited } from './plan-limits';
import { UsageMeter } from './UsageMeter';
import { usePlanUsage } from './usePlanUsage';
import { useUpgrade } from './useUpgrade';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';

/**
 * "Free plan", as a pill — and, one click away, what is left of it.
 *
 * The at-rest cue that the vendor is on Free. It says so once, in the rail,
 * and keeps the numbers in a popover: the meters matter when the vendor is
 * wondering, not on every page they open.
 */
export function PlanChip({
  planName,
  keys = CHIP_METER_KEYS,
  className,
  align = 'start',
}: {
  planName?: string;
  keys?: PlanLimitKey[];
  className?: string;
  align?: 'start' | 'center' | 'end';
}) {
  const { t } = useTranslation();
  const upgrade = useUpgrade();
  const [open, setOpen] = useState(false);
  const { data, loading, meter } = usePlanUsage();
  // On a trial the chip names the trial, not Free (docs/pricing-v2.md).
  const vp = useVendorPlan();
  const name = planName || (vp?.isTrial ? vp.planName : t('FREE_PLAN.PLAN_FREE'));

  const meters = keys
    .map((key) => ({ key, pair: meter(key) }))
    .filter((m): m is { key: PlanLimitKey; pair: { current: number; limit: number } } => !!m.pair && !isUnlimited(m.pair.limit));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="plan-chip"
          className={cn(
            'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground',
            className,
          )}
        >
          {t('FREE_PLAN.CHIP.LABEL', { plan: name })}
          <ChevronDown className="size-3" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-72 p-4" data-testid="plan-chip-popover">
        <p className="text-sm font-medium">{t('FREE_PLAN.CHIP.TITLE', { plan: name })}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('FREE_PLAN.CHIP.SUBTITLE')}</p>
        <div className="mt-3 flex flex-col gap-3">
          {loading && !data && <p className="text-xs text-muted-foreground">{t('FREE_PLAN.CHIP.LOADING')}</p>}
          {meters.map(({ key, pair }) => (
            <UsageMeter key={key} limitKey={key} current={pair.current} limit={pair.limit} showUpgrade={false} />
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="mt-4 w-full"
          onClick={() => {
            setOpen(false);
            upgrade();
          }}
        >
          {t('FREE_PLAN.SEE_PLANS')}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
